import { axios } from "@/lib/axios";

/**
 * 获取金额充值列表
 * @param useType 使用类型：1=fiat, 2=crypto
 * @returns 返回 { minAmount, maxAmount, giveScale, amounts[] }
 */
export function apiGetBalanceList( useType: 1 | 2 ) {
    return axios.post( "/api/front/pay/balance/list", { useType } );
}

/**
 * 获取充值活动区间列表
 * @param useType 使用类型：1=fiat, 2=crypto
 * @returns 返回区间配置列表，每个区间包含 { minAmount, maxAmount, giveScale }
 */
export function apiGetRechargeActivityList( useType: 1 | 2 ) {
    return axios.post( "/api/front/pay/activity/recharge/list", { useType } );
}

/**
 * 获取加密货币列表
 * @returns 返回加密货币列表
 */
export function apiGetCryptoCurrencies() {
    return axios.post( "/api/front/pay/crypto/currencies", {} );
}

/**
 * 获取支持的加密货币币种配置
 * @returns 返回币种配置列表
 */
export function apiGetCryptoCoinList() {
    return axios.post( "/api/front/pay/crypto/coin/list", {} );
}

/**
 * CoinPal-获取CoinPal支付方式信息
 * @returns 返回支付方式配置（含最小充值金额等）
 */
export function apiGetCoinPalPayType() {
    return axios.post( "/api/front/pay/coinpal/paytype", {} );
}

/**
 * 获取商户已启用的加密货币列表
 * @returns 返回商户已启用的加密货币列表
 */
export function apiGetMerchantCoins() {
    return axios.post( "/crypto/merchant-coins" );
}

/**
 * 生成加密货币充值地址
 * @param params 生成地址参数
 * @param params.payCurrency 币种（如 "eth", "btc", "usdt"）
 * @param params.rechargeBalance 充值金额（必填）
 */
export function apiGenerateCryptoAddress( params: { payCurrency: string; rechargeBalance: string | number } ) {
    return axios.post( "/api/front/pay/crypto/generate-address", {
        channel: "h5",
        channelId: "yes_no",
        versionCode: "999",
        ...params,
    } );
}

/**
 * 提交充值请求（UPI充值链接接口）
 * @param params 充值参数
 * @param params.rechargeBalance 充值金额（必填）
 * @param params.payCurrency 币种（如 "USD"）
 * @param params.payCategory 1=fiat, 2=crypto
 * @param params.payTypeId 支付方式ID（可选，fiat 需要，crypto 不需要）
 * @param params.balanceId 金额充值列表的ID（可选，已废弃，只需传 rechargeBalance）
 * @param params.channel 渠道（默认 "h5"）
 * @param params.channelId 渠道ID（可选）
 * @param params.deviceCode 设备码（可选）
 * @param params.versionCode 版本号（可选）
 * @param params.payTag 支付标签（可选，默认 "PAYMENT_CENTER_PAY"）
 */
export function apiSubmitDeposit(
    params: {
        rechargeBalance: string | number;
        payCurrency: string;
        payCategory: 1 | 2;
        payTypeId?: string | number;
        balanceId?: string | number;
        channel?: string;
        channelId?: string;
        deviceCode?: string;
        versionCode?: string;
        payTag?: string;
    },
) {
    return axios.post( "/api/front/pay/income", { channel: "h5", payTag: "PAYMENT_CENTER_PAY", ...params } );
}

/**
 * 加密货币-获取最低支付金额
 * @param params 参数
 * @param params.currencyFrom 源币种（如 "USD"）
 * @param params.currencyTo 目标币种（如 "eth", "btc", "usdt"）
 */
export function apiCheckCryptoEstimate( params: { currencyFrom: string; currencyTo: string } ) {
    return axios.post( "/api/front/pay/crypto/min-amount", { ...params } );
}

/**
 * 获取支付配置信息
 * 包括：汇率、充值手续费、提现手续费等
 * @returns 返回支付配置数据
 */
export function apiGetExchangeRate() {
    return axios.post( "/api/front/pay/config", {} );
}

/**
 * 获取交易记录列表
 * @param params 查询参数
 * @param params.pageNum 页码
 * @param params.pageSize 每页数量
 * @param params.type 交易类型（可选）：1=充值, 2=提现, 3=交易等
 */
export function apiGetTransactionHistory( params: { pageNum: number; pageSize: number; type?: number } ) {
    return axios.post( "/api/front/user/wallet/transaction/list", params );
}

// 加密货币-加密货币账户提现
// /api/front/pay/crypto/paid
export function apiCryptoWithdraw( params: {
    payCurrency: string;
    payAddress: string | number; // 提现地址
    amount: string | number; // 提现金额
} ) {
    return axios.post( "/api/front/pay/crypto/paid", params );
}
// 获取提现手续费
// /api/front/pay/withdrawal/fee
export function apiGetWithdrawalFee( params: {
    payCurrency: string;
    amount: string | number; // 提现金额
} ) {
    return axios.post( "/api/front/pay/withdrawal/fee", {
        channel: "h5",
        channelId: "yes_no",
        versionCode: "999",
        ...params,
    } );
}

// 交易明细- 用户资金明细类型
// /api/front/user/balance/types
// 获取account 对应的FilterType
//  获取 type FilterType = "all" | "credit" | "debit";
export function apiGetTransactionTypes() {
    return axios.post( "/api/front/user/balance/types", {} );
}

// account 用户资金明细
// /api/front/user/balance
// changeType 参数
// yearMonth 年月
export function apiGetUserBalance( params: { changeType: string; yearMonth?: string } ) {
    return axios.post( "/api/front/user/balance", params );
}

// withdraw 提现记录
// 账户提现记录
// /api/front/pay/paid/log
// 滚动分页
// 后端状态枚举：
// IN_REVIEW(0, 0, "待审核"),
// PROCESSING(1, 1, "处理中"),
// COMPLETED(2, 2, "已完成"),
// REJECTED(3, 3, "已拒绝"),
// FAILED(4, 4, "代付失败"),
export function apiGetWithdrawalLog( params: {
    pageNo: number;
    pageSize: number;
    status?: "PROCESSING" | "IN_REVIEW" | "COMPLETED" | "FAILED" | "REJECTED"; // 前端状态字符串
} ) {
    // 将前端状态字符串映射为后端数字状态
    const statusMap: Record<string, number | undefined> = {
        IN_REVIEW: 0,
        PROCESSING: 1,
        COMPLETED: 2,
        REJECTED: 3,
        FAILED: 4,
    };
    const backendStatus = params.status ? statusMap[params.status] : undefined;

    return axios.post( "/api/front/pay/paid/log", { ...params, status: backendStatus } );
}

// recharge 充值记录
// /api/front/pay/recharge/log
export function apiGetRechargeLog( params: {
    pageNo: number;
    pageSize: number; // 滚动分页
    changeType: string; // 类型
    status?: "success" | "pending" | "failed"; // 前端状态：success=COMPLETE, pending=PROCESS, failed=FAILED
} ) {
    // 将前端状态映射为后端状态
    const statusMap: Record<string, string> = { success: "COMPLETE", pending: "PROCESS", failed: "FAILED" };
    const backendStatus = params.status ? statusMap[params.status] : undefined;

    return axios.post( "/api/front/pay/recharge/log", { ...params, status: backendStatus } );
}

// 加密货币-查询支付状态
// /api/front/pay/crypto/payment/status
export function apiGetCryptoPaymentStatus( params: { paymentId: string } ) {
    return axios.post( "/api/front/pay/crypto/payment/status", params );
}

// 转账手续费
export function apiGetTransferFee( params: { payCurrency: string; amount: string | number } ) {
    return axios.post( "/api/front/pay/transfer/fee", {
        channel: "h5",
        channelId: "yes_no",
        versionCode: "999",
        ...params,
    } );
}

// 转账提交
export function apiTransfer( params: { payCurrency: string; payAddress: string; amount: string | number } ) {
    return axios.post( "/api/front/pay/transfer", {
        channel: "h5",
        channelId: "yes_no",
        versionCode: "999",
        ...params,
    } );
}
